/**
 * Every record, in one list.
 *
 * Rows rather than a table: merchant, date, person and amount fit a phone,
 * and a six-column table did not. The whole archive is fetched once and
 * filtered here, so search is instant; at household volume that is a few
 * hundred small records.
 *
 * A bar or a row on Trends leads here with a scope in the address (a span of
 * months, a type, a place, a person). The list then holds exactly the records
 * that bar counted, and says so in one line that also clears it.
 */
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApi } from '../hooks.js';
import { Empty, ErrorBox, Icon, Money, PageHead, Skeleton, formatDate, formatMoney } from '../ui.js';
import {
  STATUS_FILTERS,
  STATUS_LABEL,
  attention,
  byPriority,
  expenseTitle,
  matchesFilter,
  sourceIcon,
  type StatusFilter,
} from '../labels.js';
import { inScope, isFiled, placesOf, scopeFrom, scopeLabel } from '../trends.js';
import type { AppConfigResponse, CoveredPerson, Expense } from '../types.js';

export function Expenses() {
  const [params, setParams] = useSearchParams();
  const requested = params.get('status') as StatusFilter | null;
  const status: StatusFilter = STATUS_FILTERS.some((f) => f.id === requested) ? requested! : 'all';

  const all = useApi<{ items: Expense[] }>('/api/expenses?status=all');
  const persons = useApi<{ persons: CoveredPerson[] }>('/api/persons');
  const config = useApi<AppConfigResponse>('/api/config');

  const [query, setQuery] = useState('');
  const [person, setPerson] = useState('');
  const [year, setYear] = useState('');

  const items = all.data?.items ?? [];
  const scope = useMemo(() => scopeFrom(params), [params]);
  // Places are worked out from the filed records, as Trends does, so a place means the same thing here.
  const places = useMemo(() => (scope ? placesOf(items.filter(isFiled)) : null), [scope, items]);
  const pool = useMemo(
    () => (scope && places ? items.filter((e) => inScope(e, scope, places)) : items),
    [items, scope, places],
  );
  const scopeText = scope && places ? scopeLabel(scope, { people: persons.data?.persons ?? [], places: places.names }) : '';
  const established = config.data?.config.hsa_establishment_date ?? null;
  const nameOf = useMemo(
    () => new Map((persons.data?.persons ?? []).map((p) => [p.person_id, p.display_name])),
    [persons.data],
  );
  const years = useMemo(
    () => [...new Set(items.map((e) => (e.date_of_service ?? '').slice(0, 4)).filter(Boolean))].sort().reverse(),
    [items],
  );

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = pool.filter(
      (e) =>
        matchesFilter(e, status) &&
        (person === '' || e.patient_id === person) &&
        (year === '' || (e.date_of_service ?? '').startsWith(year)) &&
        (needle === '' ||
          [e.provider_name, e.service_description, e.notes]
            .filter(Boolean)
            .some((v) => v!.toLowerCase().includes(needle))),
    );
    // The waiting list is worked from the top, so what needs a decision leads.
    return status === 'draft' ? [...filtered].sort(byPriority(established)) : filtered;
  }, [pool, status, person, year, query, established]);

  const total = shown.reduce((sum, e) => sum + (e.amount_claimed_cents ?? 0), 0);
  const count = (f: StatusFilter) => pool.filter((e) => matchesFilter(e, f)).length;

  if (all.error) return <ErrorBox error={all.error} />;

  return (
    <>
      <PageHead title="Expenses" />

      {scope ? (
        <div className="scope">
          <span>
            <strong>{scopeText || 'From Trends'}</strong> <span className="muted">· filed expenses only</span>
          </span>
          <Link className="btn quiet" to="/expenses">
            Show everything
          </Link>
        </div>
      ) : null}

      <input
        type="search"
        placeholder="Search merchant or description"
        aria-label="Search expenses"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      <div className="chips scroll" role="group" aria-label="Filter by status">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.id}
            className="chip"
            aria-pressed={status === f.id}
            onClick={() => {
              // Keep a scope from Trends; change only the status.
              const next = new URLSearchParams(params);
              if (f.id === 'all') next.delete('status');
              else next.set('status', f.id);
              setParams(next, { replace: true });
            }}
          >
            {f.label}
            {all.data ? <span className="count">{count(f.id)}</span> : null}
          </button>
        ))}
      </div>

      {(persons.data?.persons.length ?? 0) > 1 || years.length > 1 ? (
        <div className="fields pair">
          <select aria-label="Filter by person" value={person} onChange={(e) => setPerson(e.target.value)}>
            <option value="">Everyone</option>
            {(persons.data?.persons ?? []).map((p) => (
              <option key={p.person_id} value={p.person_id}>
                {p.display_name}
              </option>
            ))}
          </select>
          <select aria-label="Filter by year" value={year} onChange={(e) => setYear(e.target.value)}>
            <option value="">Every year</option>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {all.loading ? (
        <Skeleton rows={5} />
      ) : shown.length === 0 ? (
        <Empty>
          <p>{items.length === 0 ? 'No expenses yet.' : 'Nothing matches.'}</p>
          {items.length === 0 ? (
            <Link className="btn primary" to="/add">
              <Icon name="plus" />
              Add a receipt
            </Link>
          ) : null}
        </Empty>
      ) : (
        <>
          <p className="xs muted">
            {shown.length} expense{shown.length === 1 ? '' : 's'} · {formatMoney(total)}
          </p>
          <div className="list">
            {shown.map((e) => (
              <ExpenseRow
                key={e.expense_id}
                expense={e}
                person={e.patient_id ? (nameOf.get(e.patient_id) ?? null) : null}
                established={established}
                showStatus={status === 'all'}
              />
            ))}
          </div>
        </>
      )}
    </>
  );
}

function ExpenseRow({
  expense: e,
  person,
  established,
  showStatus,
}: {
  expense: Expense;
  person: string | null;
  established: string | null;
  showStatus: boolean;
}) {
  const flag = attention(e, established);
  const notClaiming = e.status === 'excluded' || e.status === 'void';
  return (
    <Link className="item" to={`/expenses/${e.expense_id}`}>
      <span className="tile">
        <Icon name={sourceIcon(e.source)} />
      </span>
      <span>
        <div className="title">{expenseTitle(e)}</div>
        <div className="sub">
          {flag ? (
            <>
              <span className={`tone-${flag.tone}`}>
                <span className={`dot ${flag.tone}`} />
                {flag.label}
              </span>
              {e.service_description && e.service_description !== expenseTitle(e) ? ` · ${e.service_description}` : ''}
            </>
          ) : (
            [
              formatDate(e.date_of_service),
              person,
              e.source === 'amazon_import' ? 'Amazon' : e.service_description,
            ]
              .filter(Boolean)
              .join(' · ')
          )}
        </div>
      </span>
      <span className="end" style={notClaiming ? { textDecoration: 'line-through', opacity: 0.6 } : undefined}>
        <Money cents={e.amount_claimed_cents} />
        {/* A filed, clean record gets no label at all. */}
        {showStatus && e.status !== 'verified' ? (
          <div className="sub" style={{ textDecoration: 'none' }}>
            {STATUS_LABEL[e.status]}
          </div>
        ) : null}
      </span>
    </Link>
  );
}
